import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Download, Search, Check, X, Eye, EyeOff, Edit2, RefreshCw, Shield, Mail, Save, Clock, Server, Database, AlertTriangle, CheckCircle, XCircle, FileText, Settings, FileSpreadsheet, FileDown } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import * as XLSX from 'xlsx';

import { exportAPI, costingsAPI, quotationsAPI, partsAPI } from '../../services/api';

export function AdminExportsPage() {
  const [exporting, setExporting] = useState({
    costings: false,
    quotations: false,
    parts: false
  });

  // Client-side Excel export using SheetJS
  const handleExport = async (type, format = 'xlsx') => {
    setExporting(prev => ({ ...prev, [type]: true }));
    try {
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      let data = [];
      let filename = '';
      let sheetName = '';
      
      // Fetch data from API
      switch (type) {
        case 'costings':
          const costingsRes = await costingsAPI.list({ limit: 10000 });
          data = (costingsRes.data.items || []).map(c => ({
            'Costing Number': c.costing_number,
            'Part Number': c.part_internal_no,
            'Part Description': c.part_description,
            'Version': c.version_number,
            'Status': c.status,
            'Quantity': c.quantity,
            'RM Cost/Pc': c.rm_cost_per_piece,
            'Process Cost/Pc': c.total_process_cost_per_piece,
            'Packaging Cost/Pc': c.packaging_cost_per_piece,
            'Logistics Cost/Pc': c.logistics_cost_per_piece,
            'Total Cost/Pc': c.final_cost_per_piece,
            'Selling Price/Pc': c.selling_price_per_piece,
            'Profit Margin %': c.profit_margin_percentage,
            'Created Date': c.created_at ? new Date(c.created_at).toLocaleDateString() : ''
          }));
          filename = `costings_export_${dateStr}`;
          sheetName = 'Costings';
          break;
          
        case 'quotations':
          const quotationsRes = await quotationsAPI.list({ limit: 10000 });
          data = (quotationsRes.data.items || []).map(q => ({
            'Quotation Number': q.quotation_number,
            'Customer Name': q.customer_name,
            'Customer Code': q.customer_code,
            'Status': q.status,
            'Items Count': (q.items || []).length,
            'Currency': q.currency,
            'Exchange Rate': q.exchange_rate,
            'Subtotal': q.subtotal,
            'Tooling Cost': q.tooling_cost,
            'Sample Cost': q.sample_cost,
            'Total Amount': q.total_amount,
            'Validity Date': q.validity_date ? new Date(q.validity_date).toLocaleDateString() : '',
            'Created Date': q.created_at ? new Date(q.created_at).toLocaleDateString() : ''
          }));
          filename = `quotations_export_${dateStr}`;
          sheetName = 'Quotations';
          break;
          
        case 'parts':
          const partsRes = await partsAPI.list({ limit: 10000 });
          data = (partsRes.data.items || []).map(p => ({
            'Internal Part No': p.internal_part_no,
            'Customer Part No': p.customer_part_no,
            'Description': p.description,
            'Part Type': p.part_type,
            'Product Type': p.product_type,
            'Standard': p.standard,
            'Grade': p.grade,
            'Finish': p.finish,
            'Net Weight (g)': p.net_weight_grams,
            'Gross Weight (g)': p.gross_weight_grams,
            'Status': p.is_active ? 'Active' : 'Inactive',
            'Created Date': p.created_at ? new Date(p.created_at).toLocaleDateString() : ''
          }));
          filename = `parts_master_export_${dateStr}`;
          sheetName = 'Parts';
          break;
          
        default:
          throw new Error('Invalid export type');
      }

      if (data.length === 0) {
        toast.warning(`No ${type} data to export`);
        return;
      }

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Auto-size columns
      const maxWidth = 30;
      const colWidths = Object.keys(data[0] || {}).map(key => ({
        wch: Math.min(maxWidth, Math.max(key.length, ...data.map(row => String(row[key] || '').length)))
      }));
      worksheet['!cols'] = colWidths;

      // Generate file
      if (format === 'xlsx') {
        XLSX.writeFile(workbook, `${filename}.xlsx`);
      } else {
        // CSV format
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${filename}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      }
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully (${data.length} records)`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    } finally {
      setExporting(prev => ({ ...prev, [type]: false }));
    }
  };

  const exportCards = [
    {
      id: 'costings',
      title: 'Costings Export',
      description: 'Export all costing records with full breakdown details',
      icon: '📊',
      fields: ['Part Description', 'Version', 'Status', 'Quantity', 'RM Cost', 'Process Cost', 'Total Cost', 'Selling Price', 'Margin', 'Created Date']
    },
    {
      id: 'quotations',
      title: 'Quotations Export',
      description: 'Export all quotations with customer and line item details',
      icon: '📄',
      fields: ['Quotation #', 'Customer', 'Status', 'Items Count', 'Subtotal', 'Tax', 'Total Amount', 'Currency', 'Validity Date']
    },
    {
      id: 'parts',
      title: 'Parts Master Export',
      description: 'Export complete parts database with historical costing data',
      icon: '🔩',
      fields: ['Part Code', 'Customer Part #', 'Type', 'Standard', 'Grade', 'Finish', 'Weight', 'First Quoted', 'Last Quoted']
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
          Data Exports
        </h1>
        <p className="text-muted-foreground">Export system data to Excel or CSV format</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {exportCards.map((card) => (
          <Card key={card.id} className="card-dashboard">
            <CardHeader>
              <div className="flex items-center gap-3">
                <span className="text-3xl">{card.icon}</span>
                <div>
                  <CardTitle className="text-lg font-heading">{card.title}</CardTitle>
                  <CardDescription className="text-sm">{card.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-2">Included Fields:</p>
                  <div className="flex flex-wrap gap-1">
                    {card.fields.map((field) => (
                      <Badge key={field} variant="outline" className="text-xs">
                        {field}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    className="flex-1 btn-primary"
                    onClick={() => handleExport(card.id, 'xlsx')}
                    disabled={exporting[card.id]}
                    data-testid={`export-${card.id}-xlsx-btn`}
                  >
                    {exporting[card.id] ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                    )}
                    Excel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleExport(card.id, 'csv')}
                    disabled={exporting[card.id]}
                    data-testid={`export-${card.id}-csv-btn`}
                  >
                    <FileDown className="mr-2 h-4 w-4" />
                    CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg font-heading uppercase">Export Guidelines</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• <strong>Excel (.xlsx)</strong> - Recommended for data analysis, filtering, and reporting</p>
          <p>• <strong>CSV (.csv)</strong> - Use for importing into other systems or databases</p>
          <p>• All exports include the complete dataset based on your access level</p>
          <p>• Exported files are date-stamped for easy reference</p>
          <p>• Sensitive data is protected based on role permissions</p>
        </CardContent>
      </Card>
    </div>
  );
}

// Dropdown Manager Page for Part Types and Standards

export default AdminExportsPage;
